import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLoaderContext } from '../../../../contexts/LoaderContext';
import { useEffect, useMemo, useState } from 'react';
import { IModalConfig } from '../../../../types/modal';
import toast from '../../../../utils/toast';
import { MarsApiService as api } from '../../../../api/mars-api-service';
import { MarsApiConfig as apiConfig } from '../../../../api/mars-api-config';
import Modal from '../../../../components/common/modal/Modal';
import Button from 'devextreme-react/button';
import icons from '../../../../components/common/icons/icons';
import countryList from '../../../../components/Country.json';
import { useTranslation } from 'react-i18next';
import ScrollView from 'devextreme-react/scroll-view';
import { useNavigate } from 'react-router-dom';
import SelectBox from 'devextreme-react/select-box';

export default function VesselLiveTrackingModal({
  searchTerm,
  onClose,
}: {
  searchTerm: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { setIsLoading } = useLoaderContext();
  const [vesselGlobalSearchList, setVesselGlobalSearchList] = useState<any[]>(
    []
  );
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const [perPageCount, setPerPageCount] = useState(10);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(perPageCount);
  const [disableNext, setDisableNext] = useState(false);
  const [disablePrev, setDisablePrev] = useState(false);

  useEffect(() => {
    if (take && skip >= 0) {
      if (searchTerm) {
        vesselGlobalSearch(searchTerm);
      }
    }
  }, [searchTerm, take, skip]);

  useEffect(() => {
    setSkip(0);
    setTake(perPageCount);
    setDisableNext(false);
    setDisablePrev(true);
  }, [searchTerm]);

  const modalConfig: IModalConfig = {
    width: 1210,
    height: 600,
    visible: true,
    showTitle: false,
    closeOnOutsideClick: false,
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const selectVessel = (imo: string, iso3: string) => {
    setSearchParams(
      {
        selectedImo: imo,
        t: Date.now().toString(),
      },
      {
        state: { iso3: iso3 },
      }
    );
    setTimeout(() => {
      setSearchParams({ selectedImo: imo }, { replace: true, state: { iso3 } });
    }, 100);
    onClose();
  };

  const popupAttributes = useMemo(() => {
    return {
      id: 'vesselLiveTrackingModal',
      class: 'm-l-modal-main__wrap m-l-add-new-modal',
    };
  }, []);

  const createSearchQry = (searchText: string, skip: number, take: number) => {
    return `?searchText=${searchText}&skip=${skip}&take=${perPageCount}&requireTotalCount=true`;
  };

  const setPerPageNum = (value: number) => {
    setPerPageCount(value);
    if (value > totalCount) {
      setTake(totalCount);
      setDisableNext(true);
    } else {
      setDisableNext(false);
      setTake(value);
    }
    setSkip(0);
  };

  const next = () => {
    if (disableNext) return;
    const newSkip = skip + perPageCount;
    const newTake = take + perPageCount;
    setSkip(newSkip);
    if (newTake > totalCount) {
      setTake(totalCount);
      setDisableNext(true);
    } else {
      setTake(newTake);
    }
    setDisablePrev(false);
  };

  const previous = () => {
    if (disablePrev) return;
    const newSkip = skip - perPageCount;
    const newTake = take - perPageCount;
    setSkip(newSkip < 0 ? 0 : newSkip);
    setTake(newTake < perPageCount ? perPageCount : newTake);
    setDisableNext(false);
    setDisablePrev(newSkip <= 0);
  };

  const vesselGlobalSearch = async (searchText: string) => {
    if (searchText.length < 3) {
      toast.error({
        title: 'Error',
        message: t('errors.searchWithLessThan2chara'),
      });
      setVesselGlobalSearchList([]);
      setTotalCount(0);
      return;
    }

    const qry = createSearchQry(searchText, skip, take);
    setVesselGlobalSearchList([]);
    setTotalCount(0);

    await api
      .get(
        { url: `${apiConfig.vesselGlobalSearch}${qry}&isImoFiltered=true` },
        setIsLoading
      )
      .then((data) => {
        const result = data.data.map((item: any) => item.vessel);
        setVesselGlobalSearchList(result);
        setTotalCount(data.totalCount);
        if (take > data.totalCount) {
          setTake(data.totalCount);
        }
      })
      .catch(() => {
        setVesselGlobalSearchList([]);
        setTotalCount(0);
      });
  };

  const navigateVesselDetails = () => {
    navigate(`/vessel-info`, {
      state: { imoNumber: searchParams.get('selectedImo') },
    });
  };

  return (
    <Modal modalConfig={modalConfig} wrapperAttr={popupAttributes}>
      <div className="m-l-modal__header">
        <div className="m-l-modal-close-button-block">
          <Button className="app-c-btn app-c-icon-only-btn" onClick={onClose}>
            <icons.IconCross />
          </Button>
        </div>
      </div>
      <div className="m-l-modal__body">
        <ScrollView width="100%" height="100%">
          <div className="m-l-search-result-display-block u-mt0">
            <div className="m-l-results-found u-mb0">
              {totalCount} {t('labels.resultsFound')}
            </div>
          </div>
          <div className="m-l-advance-search-section">
            {vesselGlobalSearchList.length > 0 &&
              vesselGlobalSearchList.map((item: any, index: number) => {
                const filteredCountryList = countryList.find(
                  (countryItem) => countryItem.iso_3 === item?.iso3
                );
                return (
                  <div
                    className="m-l-advance-search-block"
                    key={index}
                    onClick={() => selectVessel(item.imo, item.iso3)}
                    data-testid={`vessel-item-${index}`}
                  >
                    <div className="m-l-advance-search-header-block">
                      <div className="m-l-heading">
                        <a onClick={() => navigateVesselDetails()}>
                          {item.vesselName}
                        </a>
                      </div>
                    </div>
                    <div className="row m-l-advance-search-body-block u-pb0">
                      <div className="col-md-3 col-sm-6 col-lg-3 col-xs-12">
                        <div className="m-l-advance-search-item-block mb-3">
                          <span>{t('labels.vesselType')}</span>
                          <h5>{item.vesselType}</h5>
                        </div>
                      </div>
                      <div className="col-md-2 col-sm-6 col-lg-2 col-xs-12">
                        <div className="m-l-advance-search-item-block mb-3">
                          <span>{t('labels.IMO')}</span>
                          <h5>{item.imoDisplayName}</h5>
                        </div>
                      </div>
                      <div className="col-md-2 col-sm-6 col-lg-2 col-xs-12">
                        <div className="m-l-advance-search-item-block mb-3">
                          <span>{t('labels.owner')}</span>
                          <h5>{item.owner}</h5>
                        </div>
                      </div>
                      <div className="col-md-3 col-sm-6 col-lg-3 col-xs-12">
                        <div className="m-l-advance-search-item-block mb-3">
                          <span>{t('labels.operatorName')}</span>
                          <h5>{item.operator}</h5>
                        </div>
                      </div>
                      <div className="col-md-2 col-sm-6 col-lg-2 col-xs-12">
                        <div className="m-l-advance-search-item-block mb-3">
                          <span>{t('labels.flag')}</span>
                          <div className="m-l-flag-block">
                            <img
                              src={filteredCountryList?.flag}
                              className="img-responsive"
                              alt="flag"
                              height={20}
                              width={20}
                            />
                            <span>{item.flag}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="m-l-advance-search-pagination-section">
            <div className="m-l-advance-search-pagination-result">
              <p>
                {t('labels.result')}{' '}
                <span>
                  {skip}-{take}
                </span>{' '}
                {t('labels.of')} <span>{totalCount}</span>
              </p>
            </div>
            <div className="m-l-pagination-dropdown-block">
              <a onClick={previous} data-testid="prev-page">
                {t('labels.previous')}
              </a>
              <div className="pagination-dropdown">
                <SelectBox
                  dataSource={[10, 20, 50, 100]}
                  className="m-c-input-control m-c-select-box"
                  onValueChanged={(e) => setPerPageNum(e.value)}
                  defaultValue={perPageCount}
                ></SelectBox>
              </div>
              <a onClick={next} data-testid="next-page">
                {t('labels.next')}
              </a>
            </div>
          </div>
        </ScrollView>
      </div>
    </Modal>
  );
}
